import {
  downloadCsv,
  generateCsvTemplate,
  parseCsv,
  type CsvColumnDef,
} from "@/lib/bulk-import";
import { Editor } from "@tiptap/core";
import Bold from "@tiptap/extension-bold";
import BulletList from "@tiptap/extension-bullet-list";
import Document from "@tiptap/extension-document";
import Heading from "@tiptap/extension-heading";
import Italic from "@tiptap/extension-italic";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { Markdown } from "tiptap-markdown";
import { SKILL_CATEGORIES, type SkillCategory } from "./";

// ============================================================
// Types
// ============================================================

export interface SkillRow {
  id: string;
  label: string; // mirrors name — required by ImportProgress
  name: string;
  category: SkillCategory | null;
  content: string;
}

type SkillValidationErrors = Record<string, string[]>;

// ============================================================
// Content conversion
// ============================================================

const convertContentToHtml = (content: string): string => {
  const trimmed = content.trim();
  if (!trimmed) return "";
  // If already HTML, pass through as-is
  if (trimmed.startsWith("<")) return content;

  const editor = new Editor({
    extensions: [
      Document,
      Paragraph,
      Text,
      Bold,
      Italic,
      Heading,
      BulletList,
      OrderedList,
      ListItem,
      Markdown.configure({ html: false }),
    ],
    content: trimmed,
  });
  const html = editor.getHTML();
  editor.destroy();
  return html;
};

// ============================================================
// CSV
// ============================================================

const SKILL_CSV_COLUMNS: CsvColumnDef[] = [
  { key: "name", header: "name", example: "Analyze this report" },
  { key: "category", header: "category", example: "Analysis" },
  {
    key: "content",
    header: "content",
    example:
      "When analyzing a report, focus on key metrics, identify trends, and summarize actionable insights.",
  },
];

const isValidCategory = (value: string): value is SkillCategory =>
  (SKILL_CATEGORIES as readonly string[]).includes(value);

const csvRecordToSkillRow = (record: Record<string, string>): SkillRow => {
  const name = "name" in record ? record["name"].trim() : "";
  const rawCategory = "category" in record ? record["category"].trim() : "";
  const category: SkillCategory | null = isValidCategory(rawCategory)
    ? rawCategory
    : null;
  const content = convertContentToHtml(
    "content" in record ? record["content"].trim() : "",
  );
  return {
    id: crypto.randomUUID(),
    label: name,
    name,
    category,
    content,
  };
};

export const parseSkillsFromCsv = (csv: string): SkillRow[] => {
  const records = parseCsv(csv);
  return records
    .filter((r) => "name" in r && r["name"].trim())
    .map((r) => csvRecordToSkillRow(r));
};

const generateSkillCsvTemplate = (): string =>
  generateCsvTemplate(SKILL_CSV_COLUMNS);

export const downloadSkillCsvTemplate = (): void =>
  downloadCsv(generateSkillCsvTemplate(), "skills-template.csv");

// ============================================================
// Validation
// ============================================================

export const validateSkills = (
  rows: SkillRow[],
): { validRows: SkillRow[]; errors: SkillValidationErrors } => {
  const errors: SkillValidationErrors = {};

  for (const row of rows) {
    const rowErrors: string[] = [];
    if (!row.name.trim()) rowErrors.push("Name is required.");
    if (rowErrors.length) errors[row.id] = rowErrors;
  }

  const validRows = rows.filter((r) => !(r.id in errors));
  return { validRows, errors };
};
